import { isEmpty, pick } from 'underscore';
import { ImageIntegrationData } from 'components/VideoTemplateEditor/types';
import { CropMetadata, Position, Size } from 'types';
import { Measurement, Pixels } from 'utils/measurement';
import {
  fitElement,
  hasPosition,
  measurementToPx,
  measurementToViewport,
  replaceRect,
  scale,
} from 'utils/placement';
import { updateState } from '../../shared/mutations';
import { VideoTemplateStateContent, WatermarkState } from '../../types';
import {
  DEFAULT_WATERMARK_MARGIN_DISTANCE_PX,
  DEFAULT_WATERMARK_SIZE_PX,
} from './constants';

export function scaleWatermark(
  state: VideoTemplateStateContent,
  multiplier: number,
): VideoTemplateStateContent {
  const { canvas, watermark } = state;
  const scaledPlacement = measurementToViewport(
    scale(
      measurementToPx(
        {
          ...watermark.position,
          ...watermark.size,
        },
        canvas,
      ),
      multiplier,
      { minArea: 500 },
    ),
    canvas,
  );

  return updateState(state, {
    watermark: {
      position: pick(scaledPlacement, 'left', 'top'),
      size: pick(scaledPlacement, 'height', 'width'),
    },
  });
}

export function recropWatermark(
  state: VideoTemplateStateContent,
  src: Blob,
  metadata: CropMetadata,
): VideoTemplateStateContent {
  const { watermark } = state;

  return {
    ...state,
    watermark: {
      ...watermark,
      metadata,
      url: src,
    },
  };
}

export function setInitialWatermarkPlacement(
  state: VideoTemplateStateContent,
  naturalSize: Size<Pixels>,
): VideoTemplateStateContent {
  const { canvas, watermark } = state;
  const currentPlacement = { ...watermark.position, ...watermark.size };

  const canvasPx = {
    height: new Pixels(canvas.height),
    width: new Pixels(canvas.width),
  };
  const placement = !hasPosition(currentPlacement)
    ? fitElement(naturalSize, canvasPx, 'fit')
    : replaceRect(
        measurementToPx(currentPlacement, canvasPx),
        naturalSize,
        canvasPx,
      );

  return updateState(state, {
    watermark: {
      position: measurementToViewport(
        {
          left: placement.left,
          top: placement.top,
        },
        canvas,
      ),
      size: measurementToViewport(
        {
          height: placement.height,
          width: placement.width,
        },
        canvas,
      ),
    },
  });
}

export function addWatermark(
  state: VideoTemplateStateContent,
  watermark: {
    original: string | Blob;
    src: string | Blob;
    position?: Position<Measurement>;
    size?: Size<Measurement>;
    integrationData?: ImageIntegrationData;
  },
): VideoTemplateStateContent {
  const { canvas } = state;

  // Initial position at the top right of the screen.
  const position = {
    left: new Pixels(
      canvas?.width -
        DEFAULT_WATERMARK_SIZE_PX -
        DEFAULT_WATERMARK_MARGIN_DISTANCE_PX,
    ).toUnit('vw', canvas),
    top: new Pixels(DEFAULT_WATERMARK_MARGIN_DISTANCE_PX).toUnit('vh', canvas),
  };

  const size = {
    height: new Pixels(DEFAULT_WATERMARK_SIZE_PX).toUnit('vh', canvas),
    width: new Pixels(DEFAULT_WATERMARK_SIZE_PX).toUnit('vw', canvas),
  };

  return updateState(state, {
    watermark: {
      integrationData: watermark.integrationData,
      position: isEmpty(position) ? undefined : position,
      originalUrl: watermark.original,
      size: isEmpty(size) ? undefined : size,
      url: watermark.src,
    },
  });
}

export function updateWatermark(
  state: VideoTemplateStateContent,
  patch: Partial<WatermarkState>,
): VideoTemplateStateContent {
  return updateState(state, {
    watermark: patch,
  });
}
